import type { Schemas } from '@argzombie/api-client';
import type {
  BackgroundDef,
  BasesHbFields,
  ConditionHbFields,
  HomeRulesDef,
  InventoryRulesDef,
  ItemGroupDef,
  ItemHbFields,
  LoadoutRulesDef,
  ModuleLevelHbFields,
} from '../hb/types';
import type {
  CoreStatDef,
  PerkDef,
  SkillDef,
  SkillRequirementDef,
  SkillRulesDef,
  StaminaDef,
} from '../skills/types';
import type { EffectRulesDef, StatusEffectDef, ZoneDef } from '../effects/types';
import type { HealthRulesDef } from '../health/types';
import type { NamesDef } from '../names/types';

/**
 * Squad raids (ADR 0027): shared-cs `SquadRulesDef` and its nested `SquadAiDef`. Typed here until
 * `@argzombie/api-client` is regenerated; the generated types will have the same camelCase fields.
 */
export type SquadAiDef = {
  // Combat HUD overhaul (validate/combatHud.ts): team dispositions and hunt & kill.
  defaultTeamDisposition?: string;
  holdAvoidM?: number;
  silentTakedownMaxM?: number;
  huntSweptSeenFrac?: number;
  huntForceLockedDoors?: boolean;
  huntLiftsKillCap?: boolean;
  huntHomeInOnLast?: boolean;
  huntRepathS?: number;
  huntGiveUpS?: number;
  huntAreaClearToastS?: number;
  thinkHz?: number;
  acquireMinS?: number;
  acquireMaxS?: number;
  firstShotWindowS?: number;
  fireIntervalMult?: number;
  hitChanceNear?: number;
  nearM?: number;
  hitChanceFar?: number;
  farM?: number;
  headshotChance?: number;
  retreatHealthFrac?: number;
  /** Wind (ADR 0073): a teammate falls back at this tier or worse. */
  fallBackAtTier?: string;
  killShareSoftCap?: number;
  killShareHardCap?: number;
  zombieTargetWeight?: number;
  downedTargetWeight?: number;
  biteGraceS?: number;
  noiseTaxPerFollower?: number;
  scoreChasingPlayer?: number;
  scoreNearSquad?: number;
  nearSquadM?: number;
  scoreInSector?: number;
  scoreRunner?: number;
  scoreBrute?: number;
  scorePlayersTarget?: number;
  playersTargetMinM?: number;
  scoreShared?: number;
  scorePerMeter?: number;
  meleeHitChance?: number;
  clutchHitChance?: number;
  clutchShots?: number;
  clutchCooldownS?: number;
  clutchPlayerHealthFrac?: number;
  /** Wind (ADR 0073): clutch fire when the player is at this tier or worse (or grabbed). */
  clutchAtTier?: string;
  /** Wind (ADR 0073): "hurt" = a wound or a tier drop in the last this many seconds. */
  hurtWindowS?: number;
  clutchRangeM?: number;
  lineOfFireRadiusM?: number;
  lineOfFirePastM?: number;
  reloadBelowFrac?: number;
  reloadIdleS?: number;
  killShareWindowS?: number;
  killShareMinKills?: number;
  softCapHitMult?: number;
  softCapAcquireAddS?: number;
  hardCapRangeM?: number;
  struggleShare?: number;
  struggleHitMult?: number;
  struggleWindowS?: number;
  weaponsFreeNoiseM?: number;
  stealthRevertS?: number;
  fileSpacingM?: number;
  fileSprintSpacingM?: number;
  fileCrouchSpacingM?: number;
  fileFirstM?: number;
  fanBearingDeg?: number;
  fanSideM?: number;
  fanRearM?: number;
  narrowWidthM?: number;
  doorwayM?: number;
  combatTighten?: number;
  slotToleranceM?: number;
  slotSwitchGain?: number;
  noGoHalfAngleDeg?: number;
  noGoRangeM?: number;
  minSpacingM?: number;
  matchNearM?: number;
  catchUpFarM?: number;
  catchUpMult?: number;
  sprintCatchUpMult?: number;
  crouchCatchUpMult?: number;
  teleportPathM?: number;
  teleportAfterS?: number;
  teleportCooldownS?: number;
  teleportBackMinM?: number;
  teleportBackMaxM?: number;
  attackLostS?: number;
  regroupIgnoreM?: number;
  invisibleFollowM?: number;
  rescueCooldownS?: number;
  autoReviveClearM?: number;
  orderTimeScale?: number;
  orderSlowMoCapS?: number;
  orderSlowMoRampS?: number;
  orderSlowMoCooldownS?: number;
  stickinessM?: number;
  teammateAttackers?: number;
  playerAttackersCovered?: number;
  playerAttackersAlone?: number;
  teammateNearM?: number;
  holdOffM?: number;
  offscreenWindUpS?: number;
  biteStaggerM?: number;
  fallBackFightM?: number;
  stackWallOffsetM?: number;
  stackFirstM?: number;
  stackSpacingM?: number;
  entryIntervalS?: number;
  entryHoldS?: number;
  entryPointMinM?: number;
  entryPointMaxM?: number;
  entryTimeoutS?: number;
  clearQuietS?: number;
  openNoiseM?: number;
  kickNoiseM?: number;
  kickStaggerRadiusM?: number;
  kickStaggerS?: number;
  barkGapS?: number;
  plainAcks?: number;
  contactBarkCooldownS?: number;
  reloadBarkCooldownS?: number;
  killBarkChance?: number;
  killBarkCooldownS?: number;
  idleBarkCooldownS?: number;
  peekS?: number;
};
export type SquadRulesDef = {
  raidEnergyPerExtraMember?: number;
  inPersonEnergyPerExtraMember?: number;
  travelEnergyPerKmPerExtraMember?: number;
  zombieMultPerExtraMember?: number;
  maxLiveZombiesPerExtraMember?: number;
  hordeThresholdPerExtraMember?: number;
  largeRoomsAtSquadSize?: number;
  largeRoomMinSide?: number;
  largeRoomAverageArea?: number;
  rationItemIds?: string[];
  rationsPerExtraMember?: number;
  downedBleedOutS?: number;
  downedBitesToDie?: number;
  reviveSeconds?: number;
  reviveRangeM?: number;
  reviveHealthFrac?: number;
  revivesPerMember?: number;
  /** Combat HUD overhaul G: the portrait-heal range (m). */
  remoteHealM?: number;
  ai?: SquadAiDef;
};

/**
 * Boss fights (raid overhaul Phase 4): shared-cs `BossPlateDef`, `BossPhaseDef`, `BossAttackDef` and the new `BossDef`
 * fields. Typed here until `@argzombie/api-client` is regenerated; the generated types will have the same camelCase fields.
 */
export type BossPlateDef = {
  id?: string;
  name?: string;
  zone?: string;
  health?: number;
};
export type BossPhaseDef = {
  id?: string;
  banner?: string;
  atHealthFrac?: number;
  plateBroken?: string;
  afterSpeakers?: boolean;
  attacks?: string[];
  speedMult?: number;
  shielded?: boolean;
  addsZombieId?: string;
  addsPerWave?: number;
  addsIntervalS?: number;
  addsOnNoiseM?: number;
  addsOnNoiseCooldownS?: number;
  addsMax?: number;
  visionRadiusM?: number;
  channelS?: number;
  channelInterruptDamage?: number;
  channelHordePerS?: number;
  channelCooldownS?: number;
  timerS?: number;
  timerHealFrac?: number;
  hordeForced?: boolean;
};
export type BossAttackDef = {
  id?: string;
  word?: string;
  kind?: string;
  windupS?: number;
  rangeM?: number;
  lengthM?: number;
  widthM?: number;
  radiusM?: number;
  arcDeg?: number;
  count?: number;
  damage?: number;
  stunS?: number;
  knockbackM?: number;
  cooldownS?: number;
  speedMps?: number;
  wallStunS?: number;
  puddleS?: number;
  puddleDps?: number;
  slowS?: number;
  slowMult?: number;
  coverNegates?: boolean;
  usesBiteBlock?: boolean;
};
export type BossDef = Schemas['BossDef'] & {
  /** ADR 0066: the skill-point challenge of a boss kill (1..10, default 10). */
  challenge?: number;
  arenaId?: string;
  antechamberZombieIds?: string[];
  modelScale?: number;
  moveSpeedMps?: number;
  sightRangeM?: number;
  sightHalfAngleDeg?: number;
  hearingMult?: number;
  lureMult?: number;
  knockbackResist?: number;
  weakSpotMult?: number;
  weakSpotArcDeg?: number;
  exposedMult?: number;
  stealthStaggerS?: number;
  plates?: (BossPlateDef | null)[];
  phases?: (BossPhaseDef | null)[];
  attacks?: (BossAttackDef | null)[];
  speakerCount?: number;
  speakerHealth?: number;
  speakerRingS?: number;
  speakerHordeFrac?: number;
};

/*
 * Scan triggers (ADR 0029): shared-cs `TriggerDef` (Runtime/Content/TriggerDefs.cs), `MissionDef` and `CosmeticDef`.
 * Same camelCase fields as the generated `Schemas['TriggerDef']` etc., but deliberately NULLABLE SUPERSETS of them: a
 * draft can hold an explicit `null` section, rules object or list (from a JSON edit), which the validator must report the
 * way the server does. Send a bundle to the API with `toContentBundle` (a type-only cast; the server re-validates).
 * Times are Unix milliseconds (UTC), like the C# `long?`.
 */
export type TriggerGeofence = { lat?: number; lon?: number; radiusM?: number };
export type TriggerRulesDef = {
  scope?: string;
  limit?: string;
  cooldownHours?: number;
  maxPerPlayer?: number | null;
  maxTotal?: number | null;
  activeFrom?: number | null;
  activeUntil?: number | null;
  season?: string | null;
  geofence?: TriggerGeofence | null;
  requiresLiveRun?: boolean;
  requiresApp?: boolean;
};
export type TriggerRaidBlock = {
  category?: string;
  difficulty?: number;
  lootTableId?: string;
  lootRolls?: number;
  rooms?: number;
  zombieIds?: string[] | null;
};
export type TriggerEncounterBlock = {
  kind?: string;
  bossId?: string | null;
  encounterId?: string | null;
  raid?: TriggerRaidBlock | null;
};
export type TriggerItemsBlock = { stacks?: (Stack | null)[] | null; destination?: string };
export type TriggerHealthBlock = { amount?: number; target?: string };
/** Wind (ADR 0073): `grant_wind` removes `winds` (0.5..3) of rest debt; never revives. */
export type TriggerWindBlock = { winds?: number; target?: string };
export type TriggerCurrencyBlock = { kind?: string; amount?: number };
export type TriggerCosmeticBlock = { cosmeticId?: string };
export type TriggerMissionBlock = { missionId?: string };
export type TriggerEventBlock = { type?: string; maxDistanceM?: number };
export type TriggerDef = {
  id?: string;
  action?: string;
  payloadVersion?: number;
  title?: string;
  description?: string;
  art?: string;
  codePrefix?: string;
  status?: string;
  missionId?: string | null;
  rewardsSummary?: string;
  devOnly?: boolean;
  rules?: TriggerRulesDef | null;
  encounter?: TriggerEncounterBlock | null;
  items?: TriggerItemsBlock | null;
  health?: TriggerHealthBlock | null;
  wind?: TriggerWindBlock | null;
  currency?: TriggerCurrencyBlock | null;
  cosmetic?: TriggerCosmeticBlock | null;
  mission?: TriggerMissionBlock | null;
  event?: TriggerEventBlock | null;
};
export type MissionDef = {
  id?: string;
  title?: string;
  kicker?: string;
  briefing?: string;
  art?: string;
  triggerId?: string;
  order?: number;
  visibility?: string;
  status?: string;
  scope?: string;
  rewardsSummary?: string;
  devOnly?: boolean;
  /** Multi-stage missions (scenario layer, S1-SCN): absent or empty = a single-win mission (ADR 0029). */
  stages?: Schemas['MissionStageDef'][] | null;
  /** A story issue (ADR 0042, shared-cs `IssueStoryDef`): null or absent = not a story issue. */
  story?: Schemas['IssueStoryDef'] | null;
  /** ADR 0062: the reward tiles MISSIONS draws (display only; jobs and bosses; a story issue's come from `story.rewards`). */
  rewards?: (MissionRewardDef | null)[] | null;
};
/** One reward tile (shared-cs `MissionRewardDef`, ADR 0062). C# defaults: kind item, quantity 1, tag first_clear. */
export type MissionRewardDef = {
  kind?: string;
  refId?: string;
  name?: string;
  quantity?: number;
  tag?: string;
};
export type CosmeticDef = { id?: string; name?: string; kind?: string };

/**
 * The raid loot feed's tiers (`raidRules.lootTiers`, shared-cs `LootTiersDef`, Wave 1): value per unit at or above which a
 * find is Good, Rare or Prize. Typed here until `@argzombie/api-client` is regenerated.
 */
export type LootTiersDef = { good?: number; rare?: number; prize?: number };

/** shared-cs `LootTiersDef` defaults (a bundle without `raidRules.lootTiers`). */
export const LOOT_TIER_DEFAULTS = { good: 20, rare: 45, prize: 80 } as const;

/**
 * The content bundle, with `squadRules` (see `SquadRulesDef`), `bosses` (see `BossDef`) and the scan trigger collections
 * (`triggers`, `missions`, `cosmetics`) typed locally whether or not the client has them yet.
 */
export type Bundle = Omit<
  Schemas['ContentBundle'],
  | 'squadRules'
  | 'bosses'
  | 'triggers'
  | 'missions'
  | 'cosmetics'
  | 'conditions'
  | 'items'
  | 'baseModules'
  | 'bases'
  | 'healthRules'
  | 'zombies'
  | 'recipes'
  | 'raidRules'
  | HbBundleKey
  | OtBundleKey
> & {
  /** Outta-Ten (ADR 0066, CONTRACT §2), typed locally (lib/skills/types.ts) until the client is regenerated. */
  coreStats?: (CoreStatDef | null)[] | null;
  skills?: (SkillDef | null)[] | null;
  perks?: (PerkDef | null)[] | null;
  skillRules?: SkillRulesDef | null;
  /** The NPC name pools (CONTRACT §13.5), typed locally (lib/names/types.ts). */
  names?: NamesDef | null;
  zombies?: ZombieDef[];
  recipes?: RecipeDef[];
  raidRules?: RaidRulesDef;
  /** Health intake (CONTRACT §7), typed locally with nullable blocks (lib/health/types.ts). */
  healthRules?: HealthRulesDef | null;
  squadRules?: SquadRulesDef;
  conditions?: ConditionsDef | null;
  items?: ItemDef[];
  baseModules?: BaseModuleDef[];
  bases?: (Schemas['BaseRulesDef'] & BasesHbFields) | null;
  // The homebase redesign sections (cards 3-8), typed locally (lib/hb/types.ts) until the client is regenerated.
  backgrounds?: (BackgroundDef | null)[] | null;
  homeRules?: HomeRulesDef | null;
  storageGroups?: (ItemGroupDef | null)[] | null;
  buildCategories?: (ItemGroupDef | null)[] | null;
  inventoryRules?: InventoryRulesDef | null;
  loadoutRules?: LoadoutRulesDef | null;
  statusEffects?: (StatusEffectDef | null)[] | null;
  zones?: (ZoneDef | null)[] | null;
  effectRules?: EffectRulesDef | null;
  bosses?: BossDef[];
  triggers?: (TriggerDef | null)[] | null;
  missions?: (MissionDef | null)[] | null;
  cosmetics?: (CosmeticDef | null)[] | null;
  /** Characters and paid resurrection (ADR 0069), typed locally. */
  characters?: CharactersRulesDef | null;
  resurrection?: ResurrectionRulesDef | null;
};
export type Stack = Schemas['ItemStack'];

/** The draft as the API's request type. Only the nullability of the locally typed sections differs; the server validates. */
export const toContentBundle = (b: Bundle) => b as Schemas['ContentBundle'];

/** Top-level bundle keys that hold content (everything but `version`). */
export type BundleKey = Exclude<keyof Bundle, 'version'>;

/**
 * One problem in a draft bundle, located well enough to show it next to the field.
 * - `section`: the bundle key (`items`, `raidRules`, ...).
 * - `index`: the entry index for array collections.
 * - `path`: dotted path inside the entry (or inside the section object), e.g. `weapon.ammoItemId` or
 *   `inputs.2.quantity`. Empty for the entry as a whole.
 * - `severity`: `error` mirrors a rule the server's `ContentValidator` enforces (publishing fails), and
 *   `warning` is a client-only sanity check the server does not enforce.
 */
export type Issue = {
  section: BundleKey;
  index?: number;
  path: string;
  message: string;
  severity: 'error' | 'warning';
};

/**
 * Survivor conditions (ADR 0031): shared-cs `ConditionsDef`, `ConditionDef`, `ConditionCureDef`. The generated types, but with
 * nullable entries so a draft's explicit `null`s can be validated like the server.
 */
export type ConditionCureDef = {
  itemId?: string | null;
  moduleId?: string | null;
  moduleLevel?: number;
};
export type ConditionDef = Omit<Schemas['ConditionDef'], 'id' | 'name' | 'cures'> &
  ConditionHbFields & {
    id?: string | null;
    name?: string | null;
    cures?: (ConditionCureDef | null)[] | null;
  };
export type ConditionsDef = Omit<Schemas['ConditionsDef'], 'types'> & {
  types?: (ConditionDef | null)[] | null;
};
/** shared-cs `ConditionIds.All`: the kinds the code knows; content tunes each but can't add new ones. */
export const CONDITION_IDS = ['wounded', 'laid_up', 'sick', 'bitten'] as const;

/** Mirrors `ArmorRules.MaxReduction` in packages/shared-cs. */
export const ARMOR_MAX_REDUCTION = 0.9;
/** Mirrors the other `ArmorRules` limits in packages/shared-cs (raid overhaul 06 §A.8). */
export const ARMOR_LIMITS = {
  maxBonusHealth: 100,
  maxBlocksPerRaid: 10,
  maxBlockGuardS: 1,
  minMoveSpeedMult: 0.5,
  maxMovementNoiseAddM: 5,
} as const;
/** `ArmorDef` fields whose C# default isn't 0 (a draft without them gets these). */
export const ARMOR_FIELD_DEFAULTS = { blockGuardS: 0.35, moveSpeedMult: 1 } as const;
/** Mirrors `ContentValidator.MaxItemValue`. */
export const MAX_ITEM_VALUE = 100;

/** `TeamRulesDef.MaxSquadSize`: a raid takes the whole team, and squads are built for 4 (ADR 0027). */
export const MAX_SQUAD_SIZE = 4;

/** Raid overhaul 06 content fields (armor blocks, padding and repair; item `value`), from the generated client. */
export type ArmorRepairDef = Schemas['ArmorRepairDef'];
export type ArmorDef = Schemas['ArmorDef'];
/** ADR 0066: a weapon names the weapon skill that trains and drives it (`weapon.skillId`; required once the bundle has skills[]). */
export type WeaponDef = Schemas['WeaponDef'] & { skillId?: string; manualAction?: boolean };
export type ItemDef = Omit<Schemas['ItemDef'], 'weapon'> &
  ItemHbFields & { weapon?: WeaponDef | null };
/** ADR 0066: a zombie's `challenge` (1..10, default 5) scales the skill points a kill gives. */
export type ZombieDef = Schemas['ZombieDef'] & { challenge?: number };
/** ADR 0066: reserved `requiresSkill` (validated, nothing gates in v1). */
export type RecipeDef = Schemas['RecipeDef'] & { requiresSkill?: SkillRequirementDef | null };
/** ADR 0066: `raidRules.stamina` (sprint budget and winded) and the unarmed weapon's skill. */
export type RaidRulesDef = Omit<Schemas['RaidRulesDef'], 'unarmed'> & {
  unarmed?: WeaponDef | null;
  stamina?: StaminaDef | null;
};
/** A base module with the infirmary bed fields on its levels (card 5) and the reserved `requiresSkill` (ADR 0066). */
export type BaseModuleLevelDef = Schemas['BaseModuleLevelDef'] &
  ModuleLevelHbFields & { requiresSkill?: SkillRequirementDef | null };
export type BaseModuleDef = Omit<Schemas['BaseModuleDef'], 'levels'> & {
  levels?: BaseModuleLevelDef[];
};
/** The bundle keys card 17's sections add (Omit'd from the generated type so a regenerated client doesn't clash). */
type HbBundleKey =
  | 'stats'
  | 'backgrounds'
  | 'homeRules'
  | 'storageGroups'
  | 'buildCategories'
  | 'inventoryRules'
  | 'loadoutRules'
  | 'statusEffects'
  | 'zones'
  | 'effectRules';
/** The bundle keys ADR 0066 adds (`stats` is gone: Omit'd through HbBundleKey and never re-added). */
type OtBundleKey = 'coreStats' | 'skills' | 'perks' | 'skillRules' | 'names';
/** Raid overhaul Phase 5 throwables (lures, pipe bombs, molotovs), from the generated client. */
export type ThrowableDef = Schemas['ThrowableDef'];

/** Mirrors `ThrowableKinds.All` in packages/shared-cs. */
export const THROWABLE_KINDS = ['lure', 'blast', 'fire'] as const;
/** Mirrors the `ThrowableRules` limits in packages/shared-cs. */
export const THROWABLE_LIMITS = {
  maxThrowRangeM: 40,
  maxFuseS: 10,
  maxBounce: 0.9,
  maxRadiusM: 12,
  maxDamage: 1000,
  maxNoiseRadiusM: 100,
  maxLureS: 60,
  maxFireS: 60,
  maxFireDps: 200,
} as const;
/** `ThrowableDef` fields whose C# default isn't 0 (a draft without them gets these). */
export const THROWABLE_FIELD_DEFAULTS = {
  kind: 'blast',
  throwRangeM: 12,
  falloffMin: 0.2,
  friendlyFireMult: 0.5,
} as const;

/** Mirrors `EncounterTypes.All` in packages/shared-cs. */
export const ENCOUNTER_TYPES = [
  'stash',
  'herd',
  'shortcut',
  'ambush',
  'straggler',
  'trader',
] as const;
/** shared-cs `WorldEventTypes.All` (siege from S2-WAV, rescue_call from S2-CR). */
export const WORLD_EVENT_TYPES = [
  'supply_truck',
  'horde_nest',
  'distress_call',
  'siege',
  'rescue_call',
] as const;
/** Mirrors `StoreProductKinds` in packages/shared-cs. */
/** shared-cs `TriggerActions.All` (ADR 0029 §2), with the block each one needs (same order as `ContentValidator.Blocks`). */
export const TRIGGER_ACTIONS = [
  'start_encounter',
  'grant_items',
  'grant_health',
  'grant_currency',
  'grant_cosmetic',
  'start_mission',
  'start_event',
  'grant_wind',
] as const;
export type TriggerAction = (typeof TRIGGER_ACTIONS)[number];
export type TriggerBlockKey =
  'encounter' | 'items' | 'health' | 'currency' | 'cosmetic' | 'mission' | 'event' | 'wind';
export const TRIGGER_BLOCKS: Record<TriggerAction, TriggerBlockKey> = {
  start_encounter: 'encounter',
  grant_items: 'items',
  grant_health: 'health',
  grant_currency: 'currency',
  grant_cosmetic: 'cosmetic',
  start_mission: 'mission',
  start_event: 'event',
  grant_wind: 'wind',
};
/** shared-cs `EncounterKinds.All`. */
export const ENCOUNTER_KINDS = ['boss', 'raid', 'ambush'] as const;
/** shared-cs `TriggerStatuses.All` (also missions). */
export const TRIGGER_STATUSES = ['live', 'coming_soon', 'retired'] as const;
/** shared-cs `TriggerScopes.All`. */
export const TRIGGER_SCOPES = ['account', 'run'] as const;
/** shared-cs `TriggerLimits.All`. */
export const TRIGGER_LIMITS = ['once', 'repeatable', 'unlimited'] as const;
/** shared-cs `TriggerItemDestinations.All`. */
export const TRIGGER_ITEM_DESTINATIONS = ['home', 'carried'] as const;
/** shared-cs `TriggerHealthTargets.All`. */
export const TRIGGER_HEALTH_TARGETS = ['player', 'team', 'all_with_you'] as const;
/** shared-cs `TriggerCurrencyKinds.All`. */
export const TRIGGER_CURRENCY_KINDS = ['energy_reserve'] as const;
/** shared-cs `MissionVisibilities.All`. */
export const MISSION_VISIBILITIES = ['listed', 'hidden'] as const;
/** shared-cs `MissionRewardKinds` (ADR 0062). */
export const MISSION_REWARD_KINDS = ['item', 'cosmetic', 'recruit', 'crate', 'haul'] as const;
/** shared-cs `MissionRewardTags` (ADR 0062). */
export const MISSION_REWARD_TAGS = [
  'first_clear',
  'every_clear',
  'every_run',
  'first_read',
  'bonus',
  'haul',
] as const;
/** shared-cs `ContentValidator.MaxMissionRewardTiles`. */
export const MAX_MISSION_REWARD_TILES = 6;
/** shared-cs `CosmeticKinds.All`. */
export const COSMETIC_KINDS = ['outfit', 'marker', 'diorama'] as const;
/** `ContentValidator.MaxGrantQuantity`, `MaxGrantEnergy`, `MaxGrantHealth`, `MaxEventDistanceM`. */
export const TRIGGER_LIMITS_MAX = {
  quantity: 999,
  energy: 150,
  health: 100,
  eventDistanceM: 1000,
} as const;
/** `InteriorGenerator.MinRooms` / `MaxRooms`: a trigger raid's room count. */
export const TRIGGER_RAID_ROOMS = { min: 1, max: 16 } as const;
/** `TriggerActions.CurrentPayloadVersion`: 1 for every action today. */
export const TRIGGER_PAYLOAD_VERSION = 1;

export const STORE_PRODUCT_KINDS = ['consumable', 'subscription', 'resurrection'] as const;
/** shared-cs `ContentValidator.MaxCharacterSlots` (ADR 0069 C7). */
export const MAX_CHARACTER_SLOTS = 8;
/** shared-cs `CharacterDeleteConfirms`. */
export const CHARACTER_DELETE_CONFIRMS = ['name'] as const;

// Characters and paid resurrection (ADR 0069), typed locally until the client is regenerated. Mirrors
// packages/shared-cs/Runtime/Content/CharacterRulesDefs.cs; defaults in teamsDefaults.ts.
/** shared-cs `CharactersRulesDef` (`characters`). */
export type CharactersRulesDef = {
  maxSlots?: number;
  vipSlots?: number;
  deleteConfirm?: string;
  /** A QR exchange counts for every alive character on both sides (true) or the active ones only (false). */
  friendshipsAllCharacters?: boolean;
};
/** shared-cs `ResurrectionBandDef`. */
export type ResurrectionBandDef = { minDays?: number; tier?: number };
/** shared-cs `ResurrectionKitDef`. */
export type ResurrectionKitDef = {
  foodDays?: number;
  waterDays?: number;
  magazinesForBestGun?: number;
  weaponFromLootTier?: boolean;
  bandages?: number;
  fullHealth?: boolean;
  clearConditions?: boolean;
};
/** shared-cs `ResurrectionRulesDef` (`resurrection`). Empty `bands` = the C# `DefaultBands()`. */
export type ResurrectionRulesDef = {
  bands?: (ResurrectionBandDef | null)[] | null;
  kit?: ResurrectionKitDef | null;
  showCutsceneOnce?: boolean;
};
/** A store product with ADR 0069's `tier` (kind `resurrection`), until the client is regenerated. */
export type StoreProductDef = Schemas['StoreProductDef'] & { tier?: number | null };
/** Mirrors `ItemCategories` in packages/shared-cs. Content may add more. */
export const ITEM_CATEGORIES = [
  'weapon',
  'ammo',
  'armor',
  'backpack',
  'consumable',
  'tool',
  'material',
  'throwable',
] as const;
/** Mirrors `ItemTags` in packages/shared-cs. */
export const ITEM_TAGS = ['pry', 'lockpick', 'risky'] as const;
/** Mirrors `NoiseActions` in packages/shared-cs. */
export const NOISE_ACTIONS = [
  'crouchWalk',
  'walk',
  'sprint',
  'meleeHit',
  'bow',
  'suppressedPistol',
  'pistol',
  'shotgun',
  'rifle',
  'breakLockedDoor',
] as const;

/** Bundle sections the server requires, in the order `ContentValidator` reports them missing. */
export const REQUIRED_SECTIONS = [
  'items',
  'lootTables',
  'recipes',
  'zombies',
  'roomTemplates',
  'baseModules',
  'categories',
  'noiseRadii',
  'raidRules',
  'survival',
] as const satisfies readonly BundleKey[];

/**
 * The Teams on the Map blocks (F0), in the order `ContentValidator` reports them missing (after the sections above).
 *
 * Absent vs null: the server parses a bundle into `ContentBundle`, whose C# initializers give every one of these
 * blocks a default. A key that is simply absent (say, a draft loaded from a bundle published before teams existed)
 * is therefore present with defaults on the server, and only an explicit `null` reaches `ContentValidator` as null
 * ("Bundle is missing 'teamRules'."). So here an absent block is NOT an error: it's validated as its C# defaults (see
 * `teamsDefaults.ts`), exactly like the server would, and an explicit `null` gives the missing error. The same holds
 * for fields and nested lists inside a present block: absent means the C# default, explicit `null` means missing.
 */
export const TEAMS_SECTIONS = [
  'survivorTraits',
  'teamRules',
  'discovery',
  'container',
  'convoy',
  'encounters',
  'worldEvents',
  'store',
  'squadRules',
] as const satisfies readonly BundleKey[];

/** Numeric fields the server checks for negatives, in its order. Chances are checked for 0..1 instead. */
export const ZOMBIE_NUMBERS = [
  'maxHealth',
  'moveSpeedMps',
  'attackDamage',
  'attackCooldownS',
  'hearingMultiplier',
  'sightRangeM',
  'knockbackForce',
] as const;
export const RAID_CHANCES = ['lockedDoorChance', 'openDoorChance', 'extraDoorChance'] as const;
export const RAID_NUMBERS = [
  'playerMaxHealth',
  'crouchSpeedMps',
  'walkSpeedMps',
  'sprintSpeedMps',
  'footstepIntervalS',
  'searchSeconds',
  'pryToolSearchMultiplier',
  'forceDoorSeconds',
  'lockpickSeconds',
  'zombiesPerRoomPerDifficulty',
  'playerHearingM',
  'unarmedNoiseRadiusM',
  'swapSeconds',
  'rummageSeconds',
] as const;
/** shared-cs `HeadshotRulesDef` defaults (`raidRules.headshot`), in the server's check order. */
export const HEADSHOT_DEFAULTS = {
  steadyS: 0.6,
  steadyCrouchS: 0.45,
  steadyMult: 2,
  unawareMult: 1.5,
  meleeUnawareMult: 2,
  staggerMult: 1.3,
  staggerWindowS: 0.5,
  sprintMult: 0.5,
  falloffStart: 0.4,
  falloffMin: 0.35,
  cap: 0.6,
} as const;
export const HEADSHOT_NUMBERS = Object.keys(
  HEADSHOT_DEFAULTS,
) as (keyof typeof HEADSHOT_DEFAULTS)[];
export const HEADSHOT_CHANCES = ['falloffStart', 'falloffMin', 'cap'] as const;
export const HEADSHOT_POSITIVE = [
  'steadyMult',
  'unawareMult',
  'meleeUnawareMult',
  'staggerMult',
  'sprintMult',
] as const;
/** shared-cs `StealthRulesDef` defaults (`raidRules.stealth`, raid overhaul #4), in the server's check order. */
export const STEALTH_DEFAULTS = {
  killRadiusM: 1.2,
  cooldownS: 0.25,
  engagedRadiusM: 8,
  killNoiseM: 1,
  crouchSightMult: 0.7,
  crouchNoticeMult: 1.5,
  bumpSenseM: 0.5,
  witnessAwareness: 0.75,
  decayPerS: 0.4,
  suspiciousAt: 0.5,
  alertFlashS: 0.5,
  cueRadiusM: 2.5,
  lungeM: 0.5,
  groupRadiusM: 2,
  suspiciousSweepDeg: 60,
  suspiciousSweepPeriodS: 2.5,
  searchSweepDeg: 90,
  alertedHalfAngleDeg: 60,
  alertedProximityM: 2.2,
} as const;
export const STEALTH_NUMBERS = Object.keys(STEALTH_DEFAULTS) as (keyof typeof STEALTH_DEFAULTS)[];
export const STEALTH_POSITIVE: readonly string[] = [
  'killRadiusM',
  'crouchSightMult',
  'crouchNoticeMult',
  'suspiciousSweepPeriodS',
];
export const STEALTH_CHANCES: readonly string[] = ['witnessAwareness', 'suspiciousAt'];
export const STEALTH_DEGREES: readonly string[] = ['suspiciousSweepDeg', 'searchSweepDeg'];
/** shared-cs `NoiseMeterDef` defaults (`raidRules.noiseMeter`, raid overhaul Phase 6), in the server's check order. */
export const NOISE_METER_DEFAULTS = {
  stepFillPerM: 0.008,
  fillPerM: 0.012,
  maxFillPerNoise: 0.45,
  lureFillMult: 0.5,
  recentTauS: 5,
  liveRefM: 60,
  liveDecayPerS: 1.2,
  silenceBrokenAt: 0.35,
  hordeWakeAt: 0.8,
  brokenHordeFillMult: 1.25,
  awakeHordeDecayMult: 0,
  spikeAt: 0.12,
  hearRadiusM: 14,
  hearIdleWeight: 0.15,
  hearMovingWeight: 0.3,
  hearSuspiciousWeight: 0.6,
  hearAlertedWeight: 1,
  hearHalfAt: 1,
  hearHordeWeight: 0.6,
  hearWaveWeight: 1.5,
  hearTauS: 0.8,
  heartbeatAt: 0.6,
  stillSpeedMps: 0.3,
  stillGraceS: 6,
  driftEveryS: 4,
  driftRadiusM: 14,
  driftStepM: 2.5,
  driftZombies: 1,
} as const;
export const NOISE_METER_NUMBERS = Object.keys(
  NOISE_METER_DEFAULTS,
) as (keyof typeof NOISE_METER_DEFAULTS)[];
export const NOISE_METER_POSITIVE: readonly string[] = [
  'stepFillPerM',
  'fillPerM',
  'recentTauS',
  'liveRefM',
  'hearRadiusM',
  'hearHalfAt',
];
export const NOISE_METER_CHANCES: readonly string[] = [
  'maxFillPerNoise',
  'lureFillMult',
  'silenceBrokenAt',
  'hordeWakeAt',
  'awakeHordeDecayMult',
  'heartbeatAt',
];
/** shared-cs `ZombieDef` vision and stealth defaults (raid overhaul #4), for drafts without them. */
export const ZOMBIE_STEALTH_DEFAULTS = {
  sightHalfAngleDeg: 25,
  noticeFarS: 0.9,
  noticeNearS: 0.25,
  sweepDeg: 35,
  sweepPeriodMinS: 5,
  sweepPeriodMaxS: 7,
  stealthKillS: 0,
  stealthKillNoiseM: 0,
  stealthImmune: false,
} as const;
/** shared-cs `WeaponDef` stealth defaults (raid overhaul #4), for drafts without them. */
export const WEAPON_STEALTH_DEFAULTS = {
  stealthKill: false,
  stealthKillS: 0.4,
  stealthKillNoiseM: 0,
} as const;
export const stripUndefined = <T extends object>(o: T) =>
  Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined)) as Partial<T>;
/**
 * The held meshes a weapon's `heldModel` can name (shared-cs `HeldModels.All`, in the same order: the server's message lists
 * them). Empty means the old rule by shape (melee, long gun, pistol).
 */
export const HELD_MODELS = [
  'pistol',
  'long_gun',
  'melee',
  'bat',
  'nail_bat',
  'machete',
  'bow',
  'crossbow',
  'rifle',
  'sawed_off',
  'lullaby',
  'maul',
  'smg',
  'carbine',
  'lmg',
  'spear',
  'sniper_rifle',
] as const;

/**
 * The weapon sound cues the game ships (shared-cs `WeaponCues.All`, same order): `weapon.audio.cue` outside it is a warning, and the
 * weapon plays its class sound. machete and ticket_punch are known but have no clips yet (they play the class sound too).
 */
export const WEAPON_CUES = [
  'kitchen_knife',
  'crowbar',
  'recurve_bow',
  'm9',
  'm9_suppressed',
  'pump_shotgun',
  'baseball_bat',
  'nail_bat',
  'machete',
  'crossbow',
  'hunting_rifle',
  'sniper_rifle',
  'sawed_off',
  'lullaby',
  'ticket_punch',
  'shiv',
  'spear',
  'uzi',
  'ar_carbine',
  'lmg',
] as const;

/** shared-cs `WeaponDef`/`ZombieDef` defaults for the raid overhaul's weapon fields (01 C.2), for drafts without them. */
export const PIERCE_DEFAULTS = {
  pierce: 0,
  pierceFalloff: 0.7,
  knockbackM: 0.25,
  knockbackResist: 0,
} as const;

/** The id of the synthetic bare-hands weapon (shared-cs `LoadoutRules.FistsId`); no item may use it. */
export const FISTS_ID = 'fists';
export const HORDE_NUMBERS = [
  'threshold',
  'fillPerRadiusM',
  'movementFillMultiplier',
  'decayPerS',
  'waveBaseSize',
  'waveGrowth',
  'waveCooldownS',
  'maxLiveZombies',
  'maxFillPerNoise',
] as const;
export const SURVIVAL_NUMBERS = [
  'foodPerSurvivorPerDay',
  'waterPerSurvivorPerDay',
  'starvationDaysToDeath',
  'starvationDamagePerDay',
  'baseStorageKg',
  'homeCheckInRadiusM',
  'raidStartRadiusM',
  'buildingCooldownHours',
  'raidTicketHours',
  'drivingLockoutKmh',
  'maxRecruits',
  'recruitTokenMinutes',
  'recruitPairCooldownHours',
] as const;

/** `SquadAiDef` fields the server checks for 0..1, in its order. */
export const SQUAD_AI_CHANCES = [
  'hitChanceNear',
  'hitChanceFar',
  'headshotChance',
  'retreatHealthFrac',
  'killShareSoftCap',
  'killShareHardCap',
  'meleeHitChance',
  'clutchHitChance',
  'clutchPlayerHealthFrac',
  'reloadBelowFrac',
  'softCapHitMult',
  'struggleShare',
  'slotSwitchGain',
  'killBarkChance',
] as const;

/** `SquadAiDef` distances, durations, counts and multipliers the server checks for >= 0, in its order (the score weights may be negative). */
export const SQUAD_AI_AMOUNTS = [
  'nearSquadM',
  'playersTargetMinM',
  'clutchShots',
  'clutchCooldownS',
  'clutchRangeM',
  'lineOfFireRadiusM',
  'lineOfFirePastM',
  'reloadIdleS',
  'killShareWindowS',
  'killShareMinKills',
  'softCapAcquireAddS',
  'hardCapRangeM',
  'struggleHitMult',
  'struggleWindowS',
  'weaponsFreeNoiseM',
  'stealthRevertS',
  'fileSpacingM',
  'fileSprintSpacingM',
  'fileCrouchSpacingM',
  'fileFirstM',
  'fanSideM',
  'fanRearM',
  'narrowWidthM',
  'doorwayM',
  'slotToleranceM',
  'noGoRangeM',
  'minSpacingM',
  'matchNearM',
  'teleportPathM',
  'teleportAfterS',
  'teleportCooldownS',
  'teleportBackMinM',
  'attackLostS',
  'regroupIgnoreM',
  'invisibleFollowM',
  'rescueCooldownS',
  'autoReviveClearM',
  'orderSlowMoCapS',
  'orderSlowMoRampS',
  'orderSlowMoCooldownS',
  'stickinessM',
  'teammateNearM',
  'holdOffM',
  'offscreenWindUpS',
  'biteStaggerM',
  'fallBackFightM',
  'stackWallOffsetM',
  'stackFirstM',
  'stackSpacingM',
  'entryIntervalS',
  'entryHoldS',
  'entryPointMinM',
  'entryTimeoutS',
  'clearQuietS',
  'openNoiseM',
  'kickNoiseM',
  'kickStaggerRadiusM',
  'kickStaggerS',
  'barkGapS',
  'plainAcks',
  'contactBarkCooldownS',
  'reloadBarkCooldownS',
  'killBarkCooldownS',
  'idleBarkCooldownS',
  'peekS',
] as const;

/** Mirrors the `int.MaxValue` guard in `LootRoller`. */
export const INT_MAX = 2147483647;
